import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PrimaryButton } from "@/components/admin/ui";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { CLASSES } from "@/lib/status";

export default function AddStudentAdminDialog({ parentId, open, onOpenChange, onDone }) {
  const { data: schools } = useFetch(open ? "/admin/schools" : null);
  const [form, setForm] = useState({ student_name: "", class_level: "", school_id: "none", consent_form_reference: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.class_level) return toast.error("Select a class");
    setBusy(true);
    try {
      await api.post(`/admin/parents/${parentId}/students`, { ...form, class_level: Number(form.class_level), school_id: form.school_id === "none" ? null : form.school_id });
      toast.success("Student added with offline consent");
      onOpenChange(false);
      setForm({ student_name: "", class_level: "", school_id: "none", consent_form_reference: "" });
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-2xl sm:max-w-md" data-testid="admin-add-student-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Add student</DialogTitle>
          <DialogDescription>Adds a student with a signed offline consent form recorded.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2"><Label>Student name</Label><Input required minLength={2} value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} data-testid="admin-student-name-input" /></div>
          <div className="space-y-2"><Label>Class</Label>
            <Select value={form.class_level} onValueChange={(v) => setForm({ ...form, class_level: v })}>
              <SelectTrigger data-testid="admin-student-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent className="bg-white">{CLASSES.map((c) => <SelectItem key={c} value={String(c)}>Class {c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Partner school</Label>
            <Select value={form.school_id} onValueChange={(v) => setForm({ ...form, school_id: v })}>
              <SelectTrigger data-testid="admin-student-school-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="none">No school</SelectItem>{schools?.filter((s) => s.active).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Signed consent form reference</Label><Input value={form.consent_form_reference} onChange={(e) => setForm({ ...form, consent_form_reference: e.target.value })} data-testid="admin-student-consent-ref-input" /></div>
          <PrimaryButton type="submit" busy={busy} className="w-full" data-testid="admin-add-student-submit">Add student</PrimaryButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}
